// Seletor de destinos (grupos Telegram ativos e chats WhatsApp role='destino'),
// ate MAX_DESTINATIONS. Compartilhado pelo Criar Posts e pela edicao das
// publicacoes recorrentes. A RPC tambem valida posse no servidor.

import { useMemo } from "react";
import { MessageCircle, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGroups } from "@/lib/hooks/useGroups";
import { useWhatsappChats } from "@/lib/hooks/useWhatsapp";
import type { CreateScheduledPostDeliveryInput } from "@/lib/hooks/useScheduledPosts";

export const MAX_DESTINATIONS = 10;

export interface DestinationOption {
  channel: "telegram" | "whatsapp";
  targetId: string;
  label: string;
}

export function destinationKey(option: Pick<DestinationOption, "channel" | "targetId">): string {
  return `${option.channel}:${option.targetId}`;
}

export function useDestinationOptions(): { telegram: DestinationOption[]; whatsapp: DestinationOption[] } {
  const { data: groups = [] } = useGroups();
  const { data: whatsappChats = [] } = useWhatsappChats();
  return useMemo(
    () => ({
      telegram: groups
        .filter((group) => group.status === "active")
        .map((group) => ({
          channel: "telegram" as const,
          targetId: String(group.telegram_chat_id),
          label: group.title ?? `Grupo ${group.telegram_chat_id}`,
        })),
      // role='destino' apenas: chat role='origem' e so monitorado, nunca alvo de post.
      whatsapp: whatsappChats
        .filter((chat) => chat.status === "active" && chat.role === "destino")
        .map((chat) => ({ channel: "whatsapp" as const, targetId: chat.jid, label: chat.title ?? chat.jid })),
    }),
    [groups, whatsappChats],
  );
}

/** Liga/desliga um destino respeitando o limite (sem mutar). */
export function toggleDestination(
  selected: Readonly<Record<string, DestinationOption>>,
  option: DestinationOption,
): Record<string, DestinationOption> {
  const key = destinationKey(option);
  if (selected[key]) {
    return Object.fromEntries(Object.entries(selected).filter(([k]) => k !== key));
  }
  if (Object.keys(selected).length >= MAX_DESTINATIONS) return { ...selected };
  return { ...selected, [key]: option };
}

export function toDeliveries(selected: Readonly<Record<string, DestinationOption>>): CreateScheduledPostDeliveryInput[] {
  return Object.values(selected).map((o) => ({ channel: o.channel, targetId: o.targetId, label: o.label }));
}

export function fromDeliveries(deliveries: readonly CreateScheduledPostDeliveryInput[]): Record<string, DestinationOption> {
  return Object.fromEntries(
    deliveries.map((d) => [destinationKey(d), { channel: d.channel, targetId: d.targetId, label: d.label ?? d.targetId }]),
  );
}

interface DestinationPickerProps {
  readonly selected: Readonly<Record<string, DestinationOption>>;
  readonly onChange: (next: Record<string, DestinationOption>) => void;
}

function OptionGroup({ title, icon, options, selected, onChange }: DestinationPickerProps & { title: string; icon: JSX.Element; options: DestinationOption[] }): JSX.Element | null {
  if (options.length === 0) return null;
  const limitReached = Object.keys(selected).length >= MAX_DESTINATIONS;
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {icon} {title}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const key = destinationKey(option);
          const isSelected = Boolean(selected[key]);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(toggleDestination(selected, option))}
              disabled={!isSelected && limitReached}
              aria-pressed={isSelected}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40",
                isSelected
                  ? "border-primary/40 bg-primary/10 text-primary shadow-sm"
                  : "border-border bg-transparent text-muted-foreground hover:bg-[hsl(var(--surface-raised)/0.4)] hover:text-foreground",
              )}
            >
              {option.channel === "telegram" ? <Send size={14} /> : <MessageCircle size={14} />}
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DestinationPicker({ selected, onChange }: DestinationPickerProps): JSX.Element {
  const options = useDestinationOptions();
  return (
    <div className="space-y-4">
      <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {Object.keys(selected).length}/{MAX_DESTINATIONS} selecionados
      </span>
      {options.telegram.length === 0 && options.whatsapp.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Nenhum grupo Telegram ou chat WhatsApp conectado ainda. Conecte em Grupos ou WhatsApp no menu.
        </p>
      )}
      <OptionGroup title="Telegram" icon={<Send size={12} />} options={options.telegram} selected={selected} onChange={onChange} />
      <OptionGroup title="WhatsApp" icon={<MessageCircle size={12} />} options={options.whatsapp} selected={selected} onChange={onChange} />
    </div>
  );
}
